"use client"

import React, { useState } from "react";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { CountrySelect } from "../CountrySelect";
import * as yup from "yup";
import { useFormik } from 'formik';

export default function LevelUpAndTrustSection({ certiReqd, blok, levelUpRef }) {

  // const images = [CISA, CISCO, CISSP, AWS,  CVE, OSCP, PA, QW, CREST, Soc2, ISO27001, ISO900];
  const images = blok?.Carousel?.filter(item => item.component === "asset-item")?.map(item => item.image.filename) || [];
  const header = blok?.Carousel?.find(item => item.component == "Heading") || {}

  const validationSchema = yup.object({
    firstName: yup.string().required("First Name is required"),
    lastName: yup.string().required("Last Name is required"),
    email: yup.string()
      .email("Invalid email format")
      .required("Email is required")
      .test(
        "business-email",
        "Personal email addresses are not allowed. Please use your business email.",
        value => {
          if (!value) return false;
          const personalDomains = [
            "gmail.com","yahoo.com","hotmail.com","outlook.com","aol.com","icloud.com","mail.com","protonmail.com","zoho.com","yandex.com","gmx.com","me.com","msn.com","live.com","rediffmail.com","ymail.com","inbox.com","qq.com","naver.com","163.com","126.com"
          ];
          const domain = value.split('@')[1]?.toLowerCase();
          return domain && !personalDomains.includes(domain);
        }
      ),
    companyName: yup.string().required("Company Name is required"),
    jobTitle: yup.string().required("Job Title is required"),
    country: yup.string().required("Country is required"),
    interestedIn: yup.array().of(yup.string()).required("Please select at least one"),
    message: yup.string().optional(),
  });

  const [message, setMessage] = useState({ status: false, message: "Your request has been successfully submitted. We will get in touch with you at the earliest." })
  const [open, setOpen] = useState(true);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
      jobTitle: "",
      country: "",
      interestedIn: [],
      message: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subject: "Form filled : Ready to Level Up", body: generateTable(values) }),
      });
      resetForm();
      const result = await response.json();
      if (result.success) setMessage({ status: true, message: "Your request has been successfully submitted. We will get in touch with you at the earliest." });
    },
  });

  function generateTable(formData) {
    return `<table>
      <tr>
        <td>First Name</td>
        <td>${formData.firstName}</td>
      </tr>
      <tr>
        <td>Last Name</td>
        <td>${formData.lastName}</td>
      </tr>
      <tr>
        <td>Email</td>
        <td>${formData.email}</td>
      </tr>
      <tr>
        <td>Company Name</td>
        <td>${formData.companyName}</td>
      </tr>
      <tr>
        <td>Job Title</td>
        <td>${formData.jobTitle}</td>
      </tr>
      <tr>
        <td>Country</td>
        <td>${formData.country}</td>
      </tr>
      <tr>
        <td>Interested In</td>
        <td>${formData.interestedIn.join(", ")}</td>
      </tr>
      <tr>
        <td>Message</td>
        <td>${formData.message}</td>
      </tr>
    </table>`
  }

  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });



  return (
    <div className={`mt-20 relative overflow-hidden  m-auto ${!certiReqd ? 'min-h-[1455px]' : 'min-h-[1260px]'} bg-white`}
      style={{ clipPath: "ellipse(250% 99.99% at bottom)", }}
    >
      {/* Content bg-form-circle-bg*/}
      <div ref={ref} className={`relative z-10 px-6 lg:px-20 py-32 lg:py-16 ${!certiReqd ? 'lg:pt-32' : 'lg:pt-10'} lg:pb-2`}
        style={{
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: !certiReqd ? 'center' : "48% 0%",
          minHeight: "100px",
          marginTop: 20,
        }}>
        {/* Heading */}
        {!certiReqd && <>
          <div className="text-center mb-12">
            <h2 className="font-aileron text-[24px] lg:text-[64px] font-bold bg-custom-radial bg-clip-text text-transparent 4xl:mt-20">
              {header.header}
            </h2>
          </div>
          <div className="overflow-hidden w-full relative pb-20">
            <Marquee>
              {[...images].map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`carousel-${index}`}
                  className="w-[150px] md:w-[167px] object-contain mx-6"
                />
              ))}
            </Marquee>
          </div>
        </>}
        <div className="flex justify-center">
          {/* Two sections: Form and Floating Gradient SVGs */}
          <div className={`flex  max-w-7xl justify-center items-start relative ml-0 ${certiReqd && 'lg:mt-[200px]'}`} ref={levelUpRef}>
            {/* Form Section */}
            <motion.div
              className="font-aileron w-full lg:w-1/2 rounded-lg lg:space-y-6"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="text-[24px] lg:text-[64px] font-bold bg-custom-radial bg-clip-text text-transparent mb-4 ml-[-4px] text-start lg:tracking-wide ">
                Ready to Level Up?
              </h3>
              <p className=" text-gray-600 text-left mb-10 lg:mb-0 lg:w-[80%]">
                Now that you’re ready to accelerate your security program, let’s put you in touch with one of our experts!
              </p>

              {/* Form Fields */}
              <form className="space-y-4 w-full lg:w-[80%]" onSubmit={formik.handleSubmit}>
                {/* First Name and Last Name */}
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="w-full">
                    <label htmlFor="firstName" className="block text-[#443b81] font-medium mb-2">
                      First Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                      required
                    />
                    {formik.touched.firstName && formik.errors.firstName && <p className="py-2"><span className="text-red-500">{formik.errors.firstName}</span></p>}
                  </div>
                  <div className="w-full">
                    <label htmlFor="lastName" className="block text-[#443b81] font-medium mb-2">
                      Last Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                      required
                    />
                    {formik.touched.lastName && formik.errors.lastName && <p className="py-2"><span className="text-red-500">{formik.errors.lastName}</span></p>}
                  </div>
                </div>

                {/* Business Email */}
                <div>
                  <label htmlFor="email" className="block text-[#443b81] font-medium mb-2">
                    Business Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                    required
                  />
                  {formik.touched.email && formik.errors.email && <p className="py-2"><span className="text-red-500">{formik.errors.email}</span></p>}
                </div>

                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="lg:w-[33%]">
                    <label htmlFor="companyName" className="block text-[#443b81] font-medium mb-2">
                      Company Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formik.values.companyName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                      required
                    />
                    {formik.touched.companyName && formik.errors.companyName && <p className="py-2"><span className="text-red-500">{formik.errors.companyName}</span></p>}
                  </div>
                  <div className="lg:w-[33%]">
                    <label htmlFor="jobTitle" className="block text-[#443b81] font-medium mb-2">
                      Job Title<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="jobTitle"
                      name="jobTitle"
                      value={formik.values.jobTitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                      required
                    />
                    {formik.touched.jobTitle && formik.errors.jobTitle && <p className="py-2"><span className="text-red-500">{formik.errors.jobTitle}</span></p>}
                  </div>
                  <div className="lg:w-[33%]">
                    <label htmlFor="country" className="block text-[#443b81] font-medium mb-2">
                      Country<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                      required
                    />
                    {formik.touched.country && formik.errors.country && <p className="py-2"><span className="text-red-500">{formik.errors.country}</span></p>}
                  </div>
                </div>

                {/* Interested In Section */}
                <div>
                  <p className="font-medium text-[#443b81] mb-2">Interested in...<span className="text-red-500">*</span></p>
                  <div className="space-y-2">
                    {["Attack Surface Management", "Vulnerability Intelligence", "Vulnerability Management", "Penetration Testing", "Becoming a Partner"].map((interest) => (
                      <label key={interest} className="block">
                        <input
                          type="checkbox"
                          name="interestedIn"
                          value={interest}
                          checked={formik.values.interestedIn.includes(interest)}
                          onChange={e => {
                            if (e.target.checked) {
                              formik.setFieldValue("interestedIn", [...formik.values.interestedIn, interest]);
                            } else {
                              formik.setFieldValue("interestedIn", formik.values.interestedIn.filter(i => i !== interest));
                            }
                          }}
                          className="mr-2 text-[#443b81] focus:border-1 focus:outline-none focus:border-[#5A27E7] accent-[#5A27E7]"
                        />
                        {interest}
                      </label>
                    ))}
                    {formik.touched.interestedIn && formik.errors.interestedIn && <p className="py-2"><span className="text-red-500">{formik.errors.interestedIn}</span></p>}
                  </div>
                </div>

                {/* Message Box */}
                <div>
                  <label htmlFor="message" className="block text-[#443b81] font-medium mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-[#f6f8fa] border border-gray-300 rounded-lg p-3 w-full h-24 focus:border-1 focus:outline-none focus:border-[#5A27E7]"
                  ></textarea>
                  {formik.touched.message && formik.errors.message && <p className="py-2"><span className="text-red-500">{formik.errors.message}</span></p>}
                </div>

                {/* Submit Button */}
                <div className="flex flex-col lg:flex-row items-center justify-center gap-4">
                  <button
                    type="submit"
                    className="bg-[#EF7B22] text-white px-6 py-3 rounded-lg w-full"
                  >
                    Submit
                  </button>
                  <span className="text-sm text-gray-500 text-center">
                    By submitting this form, you agree to our{" "}
                    <a href="/privacypolicy" className="text-blue-700 underline">
                      privacy policy
                    </a>
                  </span>
                </div>
                {message.status && <div className={`text-center bg-green-200 text-green-800 font-medium px-4 py-2`} style={{ color: "#016630", background: "#b9f8cf" }}>{message.message}</div>}
              </form>
            </motion.div>
            <motion.div
              className="lg:block hidden bottom-[-10%] "
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <img src={blok?.Form?.[0]?.image?.filename} alt="Two Lavender Rect" className="translate-y-[18%]" />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}